import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { getRiskColor, formatKeyForDisplay, formatArrayValue, formatBooleanValue } from '../../utils/riskUtils';

// Disease name translations
const diseaseNameMap = {
  'fabri_risk': 'Фабри',
  'mecosis_fungoides_risk': 'Грибовидный микоз',
  'sezary_syndrome_risk': 'Сезари',
  'granulomatous_white_skin_syndrome_risk': 'Синдром гранулематозной вялой кожи',
  'pagetoid_reticulosis_risk': 'Пейджетоидный ретикулез',
  'folliculotropic_mycosis_fungoides_risk': 'Фолликулотропный грибовидный микоз',
  'fabry_angiokeratomas': 'Фабри',
  'mycosis_fungoides': 'Грибовидный микоз',
  'sezary_syndrome': 'Сезари',
  'granulomatous_laxative_syndrome': 'Синдром гранулематозной вялой кожи',
  'pagetoid_reticulosis': 'Пейджетоидный ретикулез',
  'folliculotropic_mycosis_fungoides': 'Фолликулотропный грибовидный микоз',
  // Добавляем новые ключи для русской версии рисков
  'риск_фабри': 'Фабри',
  'риск_грибовидного_микоза': 'Грибовидный микоз',
  'риск_синдрома_сезари': 'Сезари',
  'риск_синдрома_гранулематозной_вялой_кожи': 'Синдром гранулематозной вялой кожи',
  'риск_пейджетоидного_ретикулеза': 'Пейджетоидный ретикулез',
  'риск_фолликулотропного_грибовидного_микоза': 'Фолликулотропный грибовидный микоз',
  // Фактические названия заболеваний на русском
  'Фабри': 'Фабри',
  'грибовидный микоз': 'Грибовидный микоз',
  'Сезари': 'Сезари',
  'синдром гранулематозной вялой кожи': 'Синдром гранулематозной вялой кожи',
  'пейджетоидный ретикулез': 'Пейджетоидный ретикулез',
  'фолликулотропный грибовидный микоз': 'Фолликулотропный грибовидный микоз'
};

// Соответствие ID заболеваний по параметру diase в URL
const diseaseIdMap = {
  '-1': 'Хантер',
  '1': 'Фабри',
  '2': 'Грибовидный микоз',
  '3': 'Сезари',
  '4': 'Синдром гранулематозной вялой кожи',
  '5': 'Пейджетоидный ретикулез',
  '6': 'Фолликулотропный грибовидный микоз'
};

// Перевод ключей на русский язык
const translationMap = {
  'diagnosis': 'Диагноз',
  'risk': 'Риск',
  'confidence': 'Уверенность',
  'differential_diagnosis': 'Дифференциальный диагноз',
  'recommendations': 'Рекомендации'
};

const SkinDiseaseRisks = ({ data, filterDiseaseId }) => {
  let risks = [];
  
  console.log('📊 SkinDiseaseRisks: анализ данных:', {
    'neuro_result_ru': !!data.neuro_result_ru,
    'neuro_result': !!data.neuro_result,
    'skin_check_ru': !!data.skin_check_ru,
    'skin_check': !!data.skin_check,
    'filterDiseaseId': filterDiseaseId
  });
  
  // Специальный случай для Хантера (diase=-1)
  if (filterDiseaseId === '-1') {
    console.log('📊 SkinDiseaseRisks: запрошен анализ только для Хантера');
    // Ищем данные о риске Хантера
    if (data.neuro_result_ru && data.neuro_result_ru.риск_хантера) {
      risks = [['риск_хантера', data.neuro_result_ru.риск_хантера]];
    } else if (data.neuro_result && data.neuro_result.hanter_risk) {
      risks = [['hanter_risk', data.neuro_result.hanter_risk]];
    } else {
      console.log('❌ SkinDiseaseRisks: нет данных о риске Хантера');
      return null;
    }
  } else {
    // Стандартный поиск рисков для других заболеваний
    if (data.skin_check_ru && data.skin_check_ru.риски) {
      // Новая структура рисков из примера
      console.log('📊 SkinDiseaseRisks: использую данные skin_check_ru.риски');
      risks = Object.entries(data.skin_check_ru.риски);
    } else if (data.skin_check && data.skin_check.neuro_risk) {
      console.log('📊 SkinDiseaseRisks: использую данные skin_check.neuro_risk');
      risks = Object.entries(data.skin_check.neuro_risk);
    } else if (data.neuro_result_ru) {
      console.log('📊 SkinDiseaseRisks: использую данные neuro_result_ru');
      risks = Object.entries(data.neuro_result_ru).filter(([key]) => 
        key !== 'риск_хантера' && key.includes('риск')
      );
    } else if (data.neuro_result) {
      console.log('📊 SkinDiseaseRisks: использую данные neuro_result');
      risks = Object.entries(data.neuro_result).filter(([key]) => 
        key !== 'hanter_risk' && key.includes('risk')
      );
    }
  }
  
  if (risks.length === 0) {
    console.log('❌ SkinDiseaseRisks: нет данных о рисках');
    return null;
  }
  
  console.log('📊 SkinDiseaseRisks: найдены риски до фильтрации:', risks);
  
  // Фильтрация рисков по ID заболевания из URL, если он указан (и не Хантер)
  if (filterDiseaseId && filterDiseaseId !== '-1') {
    const diseaseToFilter = diseaseIdMap[filterDiseaseId];
    if (diseaseToFilter) {
      console.log(`📊 SkinDiseaseRisks: фильтрация по заболеванию: ${diseaseToFilter}`);
      const filteredRisks = risks.filter(([key, value]) => {
        const diseaseName = diseaseNameMap[key] || formatKeyForDisplay(key);
        console.log(`Проверяю ${key} -> ${diseaseName} === ${diseaseToFilter}:`, diseaseName === diseaseToFilter);
        return diseaseName === diseaseToFilter;
      });
      
      console.log('📊 SkinDiseaseRisks: риски после фильтрации:', filteredRisks);
      risks = filteredRisks;
      
      if (risks.length === 0) {
        console.log(`❌ SkinDiseaseRisks: нет данных о риске для ${diseaseToFilter}`);
      }
    }
  }
  
  // Сортируем риски: сначала высокий, потом средний (возможный), затем низкий
  risks.sort((a, b) => {
    const priorityMap = {
      'высокий': 1,
      'high': 1,
      'средний': 2,
      'medium': 2,
      'возможный риск': 2,
      'possible risk': 2,
      'possible_risk': 2,
      'низкий': 3,
      'low': 3,
      'нет риска': 4,
      'no risk': 4,
      'no_risk': 4
    };
    
    const valueA = (typeof a[1] === 'string') ? a[1].toLowerCase() : a[1];
    const valueB = (typeof b[1] === 'string') ? b[1].toLowerCase() : b[1];
    
    return (priorityMap[valueA] || 99) - (priorityMap[valueB] || 99);
  });
  
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h3" gutterBottom sx={{ 
        fontSize: '1.125rem',
        color: 'text.primary',
        fontWeight: 500,
        mb: 2
      }}>
        Оценка рисков заболеваний
      </Typography>
      
      <TableContainer component={Paper} elevation={0} 
        sx={{ 
          mb: 3, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Заболевание</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Риск</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {risks.map(([key, value]) => {
              const diseaseName = diseaseNameMap[key] || formatKeyForDisplay(key);
              const riskColor = getRiskColor(value);
              
              return (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{diseaseName}</TableCell>
                  <TableCell>
                    <Chip 
                      label={value}
                      size="small"
                      sx={{ 
                        backgroundColor: riskColor,
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const SkinCharacteristics = ({ data, filterDiseaseId }) => {
  // Если запрошен только анализ Хантера, показываем специальные характеристики для Хантера
  if (filterDiseaseId === '-1') {
    // Ищем данные о Хантере
    const hunterData = {};
    
    if (data.neuro_result_ru && data.neuro_result_ru.хантер_данные) {
      console.log('📊 SkinCharacteristics: использую данные о Хантере из neuro_result_ru.хантер_данные');
      Object.assign(hunterData, data.neuro_result_ru.хантер_данные);
    } else if (data.neuro_result && data.neuro_result.hanter_data) {
      console.log('📊 SkinCharacteristics: использую данные о Хантере из neuro_result.hanter_data');
      Object.assign(hunterData, data.neuro_result.hanter_data);
    } else {
      // Если нет специальных данных для Хантера, извлекаем все связанные с Хантером данные
      if (data.neuro_result_ru) {
        console.log('📊 SkinCharacteristics: ищу данные о Хантере в neuro_result_ru');
        Object.entries(data.neuro_result_ru).forEach(([key, value]) => {
          if (key.toLowerCase().includes('хантер') && key !== 'риск_хантера') {
            hunterData[key] = value;
          }
        });
      } else if (data.neuro_result) {
        console.log('📊 SkinCharacteristics: ищу данные о Хантере в neuro_result');
        Object.entries(data.neuro_result).forEach(([key, value]) => {
          if (key.toLowerCase().includes('hanter') && key !== 'hanter_risk') {
            hunterData[key] = value;
          }
        });
      }
    }
    
    if (Object.keys(hunterData).length === 0) {
      console.log('❌ SkinCharacteristics: нет данных о характеристиках Хантера');
      return null;
    }
    
    console.log('📊 SkinCharacteristics: найдены характеристики Хантера:', hunterData);
    
    // Разделяем характеристики на простые и списочные
    const simpleCharacteristics = Object.entries(hunterData).filter(([key, value]) => {
      return typeof value !== 'object';
    });
    
    const arrayCharacteristics = Object.entries(hunterData).filter(([key, value]) => {
      return Array.isArray(value);
    });
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" gutterBottom sx={{ 
          fontSize: '1.125rem',
          color: 'text.primary',
          fontWeight: 500,
          mb: 2
        }}>
          Характеристики синдрома Хантера
        </Typography>
        
        {/* Основная таблица с простыми значениями */}
        {simpleCharacteristics.length > 0 && (
          <TableContainer component={Paper} elevation={0} 
            sx={{ 
              mb: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Характеристика</TableCell>
                  <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Значение</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {simpleCharacteristics.map(([key, value]) => {
                  const displayName = formatKeyForDisplay(key);
                  
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{displayName}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {typeof value === 'boolean' ? formatBooleanValue(value) : value}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Таблица со списочными значениями */}
        {arrayCharacteristics.length > 0 && (
          <TableContainer component={Paper} elevation={0} 
            sx={{ 
              mb: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Характеристика</TableCell>
                  <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Значения</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {arrayCharacteristics.map(([key, value]) => {
                  const displayName = formatKeyForDisplay(key);
                  const displayValue = formatArrayValue(value);
                  
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{displayName}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{displayValue}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }
  
  // Стандартное отображение характеристик для других заболеваний
  const skinCheck = data.skin_check_ru || data.skin_check;
  
  if (!skinCheck) {
    console.log('❌ SkinCharacteristics: нет данных skin_check_ru или skin_check');
    return null;
  }
  
  console.log('📊 SkinCharacteristics: обрабатываю данные:', skinCheck);
  
  // Выделяем обычные характеристики (не массивы и не объекты)
  const simpleCharacteristics = Object.entries(skinCheck).filter(([key, value]) => {
    return key !== 'риски' && key !== 'neuro_risk' && typeof value !== 'object';
  });
  
  // Выделяем списочные характеристики (массивы)
  const arrayCharacteristics = Object.entries(skinCheck).filter(([key, value]) => {
    return Array.isArray(value);
  });
  
  console.log('📊 SkinCharacteristics: найдены характеристики:', {
    'простые': simpleCharacteristics.length,
    'списки': arrayCharacteristics.length
  });
  
  if (simpleCharacteristics.length === 0 && arrayCharacteristics.length === 0) {
    console.log('❌ SkinCharacteristics: нет данных о характеристиках');
    return null;
  }
  
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h3" gutterBottom sx={{ 
        fontSize: '1.125rem',
        color: 'text.primary',
        fontWeight: 500,
        mb: 2
      }}>
        Характеристики кожного покрова
      </Typography>
      
      {/* Основная таблица с простыми значениями */}
      {simpleCharacteristics.length > 0 && (
        <TableContainer component={Paper} elevation={0} 
          sx={{ 
            mb: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Характеристика</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Значение</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {simpleCharacteristics.map(([key, value]) => {
                const displayName = formatKeyForDisplay(key);
                
                return (
                  <TableRow key={key} hover>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{displayName}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {typeof value === 'boolean' ? formatBooleanValue(value) : value}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Таблица со списочными значениями */}
      {arrayCharacteristics.length > 0 && (
        <TableContainer component={Paper} elevation={0} 
          sx={{ 
            mb: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Характеристика</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Значения</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {arrayCharacteristics.map(([key, value]) => {
                const displayName = formatKeyForDisplay(key);
                const displayValue = formatArrayValue(value);
                
                return (
                  <TableRow key={key} hover>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{displayName}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{displayValue}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const SkinDiseaseDetails = ({ data, filterDiseaseId }) => {
  // Если запрошен только анализ Хантера, не показываем детали заболеваний
  if (filterDiseaseId === '-1') {
    console.log('📊 SkinDiseaseDetails: скрыто для анализа Хантера');
    return null;
  }

  // Check if we have detailed disease assessment data
  const diseaseData = data.skin_diseases_ru || data.skin_diseases;
  
  if (!diseaseData) {
    console.log('❌ SkinDiseaseDetails: нет данных о заболеваниях');
    return null;
  }
  
  console.log('📊 SkinDiseaseDetails: данные о заболеваниях:', diseaseData);
  
  // Check if we have final diagnosis
  const finalDiagnosis = diseaseData.окончательный_диагноз || diseaseData.final_diagnosis;
  
  // Get disease risk assessment entries
  const riskAssessment = diseaseData.disease_risk_assessment || diseaseData.оценка_риска_заболеваний || {};
  
  // Также проверяем наличие информации о заболеваниях на верхнем уровне
  Object.keys(diseaseData).forEach(key => {
    if (typeof diseaseData[key] === 'object' && diseaseData[key] !== null && 
        diseaseData[key].риск && !riskAssessment[key]) {
      riskAssessment[key] = diseaseData[key];
    }
  });
  
  // Get points data
  const pointsData = diseaseData.баллы || diseaseData.points || [];
  
  if (Object.keys(riskAssessment).length === 0 && pointsData.length === 0) {
    console.log('❌ SkinDiseaseDetails: нет данных об оценке риска заболеваний и баллах');
    return null;
  }
  
  // Фильтруем данные о заболеваниях, если указан ID в URL
  let filteredRiskAssessment = riskAssessment;
  if (filterDiseaseId && filterDiseaseId !== '-1') {
    const diseaseToFilter = diseaseIdMap[filterDiseaseId];
    if (diseaseToFilter) {
      console.log(`📊 SkinDiseaseDetails: фильтрация по заболеванию: ${diseaseToFilter}`);
      filteredRiskAssessment = {};
      
      Object.entries(riskAssessment).forEach(([disease, assessment]) => {
        const diseaseName = diseaseNameMap[disease] || formatKeyForDisplay(disease);
        console.log(`Проверяю ${disease} -> ${diseaseName} === ${diseaseToFilter}:`, diseaseName === diseaseToFilter);
        if (diseaseName === diseaseToFilter) {
          filteredRiskAssessment[disease] = assessment;
        }
      });
      
      console.log('📊 SkinDiseaseDetails: результаты после фильтрации:', filteredRiskAssessment);
      
      if (Object.keys(filteredRiskAssessment).length === 0) {
        console.log(`❌ SkinDiseaseDetails: нет данных для ${diseaseToFilter}`);
        return null;
      }
    }
  }
  
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h3" gutterBottom sx={{ 
        fontSize: '1.125rem',
        color: 'text.primary',
        fontWeight: 500,
        mb: 2
      }}>
        Результаты альтернативной модели
      </Typography>
      
      {/* Отображение сводной таблицы с заболеваниями, рисками и баллами */}
      <TableContainer component={Paper} elevation={0} 
        sx={{ 
          mb: 3, 
          border: '1px solid', 
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Заболевание</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem', width: 120 }}>Риск</TableCell>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem', width: 80 }}>Баллы</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(filteredRiskAssessment).map(([disease, assessment]) => {
              const diseaseName = diseaseNameMap[disease] || formatKeyForDisplay(disease);
              
              // Получаем значение риска
              let riskValue = 'неизвестно';
              if (typeof assessment === 'string') {
                riskValue = assessment;
              } else if (assessment.риск) {
                riskValue = assessment.риск;
              } else if (assessment.risk) {
                riskValue = assessment.risk;
              }
              
              const riskColor = getRiskColor(riskValue);
              
              // Находим баллы для этого заболевания
              const pointsItem = pointsData.find(item => 
                (item.disease === disease) || 
                (diseaseNameMap[item.disease] === diseaseName)
              );
              const points = pointsItem ? (pointsItem.point || pointsItem.points || 0) : 0;
              
              return (
                <TableRow key={disease} hover>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{diseaseName}</TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>
                    <Chip 
                      label={riskValue}
                      size="small"
                      sx={{ 
                        backgroundColor: riskColor,
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{points}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const SkinResults = ({ data }) => {
  const [filterDiseaseId, setFilterDiseaseId] = useState(null);
  const [previousPhoto, setPreviousPhoto] = useState(null);
  
  // Получаем ID заболевания из URL-параметра при монтировании компонента
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const diaseParam = urlParams.get('diase');
    const currentPhoto = urlParams.get('photo');
    
    // Если фото изменилось и есть параметры фильтрации, очищаем URL
    if (previousPhoto && previousPhoto !== currentPhoto) {
      console.log('📊 SkinResults: обнаружена новая фотография, очищаем URL-параметры');
      
      // Создаем новый URL только с параметром photo
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('diase');
      newUrl.searchParams.delete('data');
      // Сохраняем только параметр photo
      newUrl.searchParams.set('photo', currentPhoto);
      
      // Обновляем URL без перезагрузки страницы
      window.history.replaceState({}, '', newUrl.toString());
      
      // Сбрасываем фильтр
      setFilterDiseaseId(null);
    } else if (diaseParam && (diseaseIdMap[diaseParam] || diaseParam === '-1')) {
      console.log(`📊 SkinResults: найден параметр diase=${diaseParam}, фильтрация по: ${diseaseIdMap[diaseParam] || 'Хантер'}`);
      setFilterDiseaseId(diaseParam);
    }
    
    // Сохраняем текущее фото для сравнения при следующем обновлении
    if (currentPhoto) {
      setPreviousPhoto(currentPhoto);
    }
  }, [data]); // Зависимость от data обеспечит выполнение эффекта при изменении данных
  
  // Проверяем наличие данных о коже и Хантере
  const hasSkinData = data && (
    data.skin_diseases_ru || data.skin_diseases || 
    data.skin_check_ru || data.skin_check || 
    data.neuro_result_ru || data.neuro_result || 
    data.skin_disease_ru || data.skin_disease
  );
  
  // Для случая с Хантером проверяем наличие специфических данных о Хантере
  const hasHunterData = filterDiseaseId === '-1' && data && (
    (data.neuro_result_ru && data.neuro_result_ru.риск_хантера) ||
    (data.neuro_result && data.neuro_result.hanter_risk)
  );
  
  console.log('📊 SkinResults: доступные данные:', {
    'skin_diseases_ru': !!data.skin_diseases_ru,
    'skin_diseases': !!data.skin_diseases,
    'skin_check_ru': !!data.skin_check_ru,
    'skin_check': !!data.skin_check,
    'neuro_result_ru': !!data.neuro_result_ru,
    'neuro_result': !!data.neuro_result,
    'skin_disease_ru': !!data.skin_disease_ru,
    'skin_disease': !!data.skin_disease,
    'фильтр по заболеванию': filterDiseaseId === '-1' ? 'Хантер' : 
                             (filterDiseaseId ? diseaseIdMap[filterDiseaseId] : 'нет')
  });
  
  if ((!hasSkinData) || (filterDiseaseId === '-1' && !hasHunterData)) {
    console.log('❌ SkinResults: нет необходимых данных');
    return null;
  }
  
  // Проверяем наличие окончательного диагноза
  const finalDiagnosis = 
    (data.skin_diseases_ru && data.skin_diseases_ru.окончательный_диагноз) || 
    (data.skin_diseases && data.skin_diseases.final_diagnosis);
  
  // Если есть фильтрация, проверяем соответствует ли финальный диагноз фильтру
  // Для случая с Хантером не показываем финальный диагноз
  let showFinalDiagnosis = false;
  if (finalDiagnosis && filterDiseaseId !== '-1') {
    if (!filterDiseaseId) {
      // Если нет фильтрации, показываем любой диагноз
      showFinalDiagnosis = true;
    } else if (diseaseIdMap[filterDiseaseId]) {
      // Проверяем, есть ли запрошенное заболевание в окончательном диагнозе
      const diseaseToFilter = diseaseIdMap[filterDiseaseId];
      console.log(`📊 SkinResults: проверка соответствия окончательного диагноза ${finalDiagnosis} фильтру ${diseaseToFilter}`);
      showFinalDiagnosis = finalDiagnosis.includes(diseaseToFilter);
    }
  }
  
  console.log('📊 SkinResults: показывать окончательный диагноз:', showFinalDiagnosis);
  
  return (
    <Box>
      {/* Заголовок для анализа Хантера */}
      {filterDiseaseId === '-1' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" gutterBottom sx={{ 
            fontSize: '1.125rem', 
            color: 'text.primary', 
            fontWeight: 500 
          }}>
            Анализ на синдром Хантера
          </Typography>
        </Box>
      )}
      
      {/* Заголовок для конкретного заболевания */}
      {filterDiseaseId && filterDiseaseId !== '-1' && diseaseIdMap[filterDiseaseId] && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" gutterBottom sx={{ 
            fontSize: '1.125rem', 
            color: 'text.primary', 
            fontWeight: 500 
          }}>
            Анализ на {diseaseIdMap[filterDiseaseId]}
          </Typography>
        </Box>
      )}
      
      {/* Отображаем окончательный диагноз, если он есть и соответствует фильтру */}
      {showFinalDiagnosis && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" gutterBottom sx={{ 
            fontSize: '1.125rem', 
            color: 'text.primary', 
            fontWeight: 500 
          }}>
            Окончательный диагноз
          </Typography>
          
          <Chip 
            label={finalDiagnosis}
            color="primary"
            sx={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
            size="small"
          />
        </Box>
      )}
      
      {/* Отображаем риски заболеваний */}
      <SkinDiseaseRisks data={data} filterDiseaseId={filterDiseaseId} />
      
      {/* Отображаем характеристики кожи (показываются всегда) */}
      <SkinCharacteristics data={data} filterDiseaseId={filterDiseaseId} />
      
      {/* Отображаем детальную информацию о заболеваниях с учетом фильтра */}
      <SkinDiseaseDetails data={data} filterDiseaseId={filterDiseaseId} />
    </Box>
  );
};

export default SkinResults; 